import { Injectable, inject } from '@angular/core';
import { SessionModalCloser } from '@core/interfaces';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ModalService } from './modal/modal.service';

/**
 * Concrete implementation of {@link SessionModalCloser}.
 *
 * Bound to `SESSION_MODAL_CLOSER` in `appConfig` (app.config.ts) so that `SessionService`
 * (which lives in `libs/core`) can close all open modals on auto-logout
 * without taking a compile-time dependency on `NgbModal` or `ModalService`.
 */
@Injectable({ providedIn: 'root' })
export class AppModalCloser implements SessionModalCloser {
    private readonly ngbModal = inject(NgbModal);
    private readonly modalService = inject(ModalService);

    /**
   * Dismisses all ng-bootstrap modals and closes every custom ModalService
   * modal. Safe to call even when there are no open modals.
   */
    closeAll(): void {
    // Isolated on purpose: a dismiss handler that throws must not take the DOM
    // sweep down with it. Bootstrap-only dialogs (opened with data-bs-toggle,
    // tracked by neither service) are reachable *only* through that sweep, so
    // losing it strands them on screen.
        try {
            this.ngbModal.dismissAll('session-ended');
        } catch { /* teardown is best-effort — keep going */ }

        // Close custom ModalService-tracked modals, then sweep the leftovers.
        this.modalService.closeAll();
    }

    /** @inheritdoc */
    setTeardownGuard(isActive: boolean): void {
        this.modalService.setTeardownGuard(isActive);
    }
}
